import { EditorMargin } from "@kaggle/components";
import {
  codeMirrorBackground,
  darkPalette,
  MaterialBreakpointDown,
  MaterialBreakpointUp,
  menuButtonBackground,
  menuButtonHoverBackground,
  scrollbars,
  shadow,
  sidebarBackground,
  windowBackground,
} from "@kaggle/core";
import {
  GREY_100,
  GREY_300,
  GREY_50,
  GREY_600,
  GREY_800,
  GREY_900,
  KAGGLE_BLACK,
  KAGGLE_WHITE,
} from "@kaggle/material";
import { createGlobalStyle } from "styled-components";

/** A special class name we add to the Jupyter cell when it is collapsed. */
export const CLASS_NAME_COLLAPSED = "_kg_hide-input";

/** A special class name we add to the Jupyter cell when it is collapsed. */
export const CLASS_NAME_FROZEN = "_kg_frozen";

/** A special class name we add to the Jupyter cell output when it is collapsed. */
export const CLASS_NAME_COLLAPSED_OUTPUT = "_kg_hide-output";

// When using these, they must be prepended with /* sc-selector */ to appease stylelint.
export const LIGHT_SELECTOR = `body[data-jp-theme-light="true"]`;
export const DARK_SELECTOR = `body[data-jp-theme-light="false"]`;
export const NOTEBOOK_SELECTOR = `body[data-kaggle-source-type="notebook"]`;
export const SCRIPT_SELECTOR = `body[data-kaggle-source-type="script"]`;

const BLACK = "rgb(0, 0, 0)";
const WHITE = "rgb(255, 255, 255)";
export const DARK_EDIT_BG = "#0A1116";
export const DARK_EDIT_BORDER = "#4f5868";
const LIGHT_CELL_BACKGROUND = "#F1F3F4";

const COLLAPSER_LIGHT = "#202124";
const COLLAPSER_LIGHT_HOVER = GREY_300;
const COLLAPSER_DARK = "#FFFFFF";
const COLLAPSER_DARK_HOVER = GREY_600;

/** Global styles to apply to Jupyter notebook. */
export const JupyterLabGlobalStyle = createGlobalStyle`
  /* needed for stylelint to accept the LIGHT_SELECTOR and DARK_SELECTOR used in this file. */
  /* stylelint-disable selector-type-no-unknown */

  /* The JL splash screen. */
  #jupyterlab-splash {
    display: none !important;
  }

  /* The notebook itself. */
  .jp-NotebookPanel {
    position: fixed !important;
    min-width: 100vw !important;
    min-height: 100vh !important;
    left: 0 !important;
    top: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
  }

  /* The notebook area that we are expanding. */
  .lm-DockPanel-widget {
    border-left: 0 !important;
    border-top: 0 !important;
    border-right: 0 !important;
    border-bottom: 0 !important;
  }

  /* The JL header area (toolbar / file menu). */
  .lm-Widget.p-Widget.jp-Toolbar.jp-scrollbar-tiny.jp-NotebookPanel-toolbar {
    width: 100% !important;
    min-height: 0 !important;
    height: 0 !important;
    display: none;
  }

  /* The JL cells area. */
  .lm-Widget.p-Widget.jp-Notebook.jp-mod-scrollPastEnd.jp-NotebookPanel-notebook {
    width: 100% !important;
    top: 0 !important;
    /* This div is getting an inline style with discrete height from JL. */
    height: auto !important;
    max-height: 100%;
  }

  /* CodeMirror wrapping element. */
  .lm-Widget.p-Widget.jp-CodeMirrorEditor.jp-Editor.jp-InputArea-editor {
    border-radius: 8px;
  }

  ${
    /* sc-selector */ LIGHT_SELECTOR
  } .lm-Widget.p-Widget.jp-CodeMirrorEditor.jp-Editor.jp-InputArea-editor {
    border: 1px solid #DADCE0 !important;
  }

  ${
    /* sc-selector */ DARK_SELECTOR
  } .lm-Widget.p-Widget.jp-CodeMirrorEditor.jp-Editor.jp-InputArea-editor {
    border: 1px solid #5F6368 !important;
  }

  /* Required to make cell button hover state background not clip. */
  .lm-Widget.p-Widget.jp-InputPrompt.jp-InputArea-prompt {
    overflow: visible;
  }

  /**
   * The next few blocks are used to style the LSP rename symbol
   * dialog. Unfortunately, the LSP dialogs don't have a custom
   * class, so these changes would apply to all dialogs inside the
   * iframe. However, we aren't likely to have any other dialogs
   * inside the iframe, so this should be fine.
   */
  .lm-Widget.p-Widget.lm-Panel.p-Panel.jp-Dialog-content {
    border-radius: 20px;
    font-family: Inter, sans-serif;
    min-width: 500px;
    padding: 32px;
    resize: none;
  }

  ${
    /* sc-selector */ DARK_SELECTOR
  } .lm-Widget.p-Widget.lm-Panel.p-Panel.jp-Dialog-content {
    background-color: rgb(32, 33, 36);
  }

  .lm-Widget.p-Widget.jp-Dialog-header {
    font-size: 32px;
    font-weight: 700;
  }

  ${/* sc-selector */ LIGHT_SELECTOR} .lm-Widget.p-Widget.jp-Dialog-header {
    color: rgb(32, 33, 36);
  }

  ${/* sc-selector */ DARK_SELECTOR} .lm-Widget.p-Widget.jp-Dialog-header {
    color: rgb(232, 234, 237);
  }

  /* jp-mod-styled = the input element for getting the new name */
  .lm-Widget.p-Widget.jp-Input-Dialog.jp-Dialog-body > .jp-mod-styled {
    background-color: transparent;
    border-radius: 4px;
    box-shadow: none;
    height: 40px;
    margin: 8px 0 32px 0;
  }

  ${
    /* sc-selector */ LIGHT_SELECTOR
  } .lm-Widget.p-Widget.jp-Input-Dialog.jp-Dialog-body > .jp-mod-styled {
    border-color: ${GREY_300};
    &:hover {
      border-color: ${GREY_900};
    }
    &:focus {
      border-color: ${GREY_900};
    }
  }

  ${
    /* sc-selector */ DARK_SELECTOR
  } .lm-Widget.p-Widget.jp-Input-Dialog.jp-Dialog-body > .jp-mod-styled {
    border-color: ${GREY_600};
    &:hover {
      border-color: ${GREY_50};
    }
    &:focus {
      border-color: ${GREY_50};
    }
  }

  .lm-Widget.p-Widget.jp-Dialog-footer > .jp-Dialog-button {
    font-size: 14px;
    font-weight: 700;
    height: 36px;
    letter-spacing: normal;
    margin: 0;
    outline: none;
    padding: 0 24px;
  }

  .lm-Widget.p-Widget.jp-Dialog-footer > .jp-Dialog-button.jp-mod-reject {
    background-color: transparent;
  }

  ${
    /* sc-selector */ LIGHT_SELECTOR
  } .lm-Widget.p-Widget.jp-Dialog-footer > .jp-Dialog-button.jp-mod-reject {
    color: ${GREY_900};
    &:hover {
      background-color: ${GREY_100};
    }
  }

  ${
    /* sc-selector */ DARK_SELECTOR
  } .lm-Widget.p-Widget.jp-Dialog-footer > .jp-Dialog-button.jp-mod-reject {
    &:hover {
      background-color: ${GREY_800};
    }
  }

  ${
    /* sc-selector */ LIGHT_SELECTOR
  } .lm-Widget.p-Widget.jp-Dialog-footer > .jp-Dialog-button.jp-mod-accept {
    background-color: ${KAGGLE_BLACK};
    color: ${KAGGLE_WHITE};
  }

  ${
    /* sc-selector */ DARK_SELECTOR
  } .lm-Widget.p-Widget.jp-Dialog-footer > .jp-Dialog-button.jp-mod-accept {
    background-color: ${KAGGLE_WHITE};
    color: ${GREY_900};
  }

  /** Fixes styling nit on documentation hover card:
      https://screenshot.googleplex.com/66xtLHMmnPi6gAX */
  ${
    /* sc-selector */ DARK_SELECTOR
  } .lsp-signature-help > .jp-Tooltip-content * {
    background-color: transparent;
  }

  /* JupyterLab adds too much padding to end of the page, which we do not
     want. */
  .jp-Notebook.jp-mod-scrollPastEnd::after {
    /* TODO(b/227634919): Add enough space to the bottom of the notebook to let
       users click "+ Code" / "+ Markdown" actions when the snackbar is visible
       to avoid b/196609396. We should revisit when we're using the global page
       snackbar instead, which may not have this issue. */
    min-height: 128px;
  }

  /* Scrollbars (light mode). */
  ${/* sc-selector */ LIGHT_SELECTOR} * {
    ${scrollbars.light}
  }

  /* Scrollbars (dark mode). */
  ${/* sc-selector */ DARK_SELECTOR} * {
    ${scrollbars.dark}
  }

  ${/* sc-selector */ DARK_SELECTOR} .jp-Notebook, ${
  /* sc-selector */ DARK_SELECTOR
} .jp-MainAreaWidget {
    ${windowBackground("#fff")};
  }

  /* Scripts require fixed position spanning full width / height. */
  ${/* sc-selector */ SCRIPT_SELECTOR} .jp-Notebook .CodeMirror.cm-s-jupyter {
    position: fixed;
    left: 0;
    top: 0;
    width: 100vw;
    max-width: 100vw;
    height: 100vh;
    max-height: 100vh;
    z-index: 999999;
    /* Ensures padding doesn't push scrollbars out of view. */
    box-sizing: border-box;
  }

  ${/* sc-selector */ DARK_SELECTOR} .CodeMirror-scrollbar-filler {
    ${windowBackground()};
  }
  ${/* sc-selector */ LIGHT_SELECTOR} .CodeMirror-scrollbar-filler {
    background-color: ${LIGHT_CELL_BACKGROUND};
  }

  /* Various gutter / line number styles. */
  .CodeMirror-gutter.CodeMirror-linenumbers {
    background: transparent !important;
  }

  ${/* sc-selector */ SCRIPT_SELECTOR} .CodeMirror-gutters {
    padding-right: 4px;
    border-right: 1px solid ${darkPalette.lightLines};
    /* Make it so scroll bar gutter doesn't jitter on horizontal move (default absolute). */
    position: fixed !important;
    left: 0 !important;
  }

  ${/* sc-selector */ SCRIPT_SELECTOR} .CodeMirror-linenumber {
    margin-left: -8px; /* Line numbers will bleed into the script. */
  }

  .CodeMirror-hscrollbar {
    cursor: default;
  }

  ${/* sc-selector */ DARK_SELECTOR} .CodeMirror-gutters {
    background: ${darkPalette.windowBackground} !important;
    border-right: 1px solid ${darkPalette.darkLines};
  }

  /* General cell modifications (padding). */
  .jp-Notebook .CodeMirror.cm-s-jupyter {
    padding: 12px 16px !important;
    font-family: 'Roboto Mono', monospace !important;
    font-size: 14px !important;
    line-height: 20px !important;
  }

  /*
  Hack to prevent auto-zoom behaviors on mobile browsers. These textarea
  elements receive keystrokes and focus, but are not visible to the user. If
  font-size is large enough, these mobile browsers will not zoom. See
  b/271131560 for details.
  */
  .jp-Notebook .CodeMirror.cm-s-jupyter textarea {
    font-size: 16px !important;
  }

  /* Make codemirror background non-transparent to prevent cell artifacts appearing behind scripts. */
  ${/* sc-selector */ LIGHT_SELECTOR} .jp-Notebook .CodeMirror.cm-s-jupyter {
    background: ${LIGHT_CELL_BACKGROUND} !important;
  }

  ${/* sc-selector */ DARK_SELECTOR} .jp-Notebook .CodeMirror.cm-s-jupyter {
    ${codeMirrorBackground()}
  }

  ${/* sc-selector */ DARK_SELECTOR} .jp-Toolbar {
    ${sidebarBackground()};
  }

  ${/* sc-selector */ DARK_SELECTOR} .jp-SideBar.p-TabBar {
    ${sidebarBackground()};
  }

  ${
    /* sc-selector */ DARK_SELECTOR
  } .jp-SideBar .p-TabBar-tab:not(.p-mod-current) { 
    ${sidebarBackground()};

    &:hover {
      ${menuButtonHoverBackground()};
    }
  }

  ${/* sc-selector */ DARK_SELECTOR} .jp-SideBar .p-TabBar-tab.p-mod-current { 
    ${menuButtonBackground()};
  }

  /* Adjust cell margins responsively. */
  .jp-Notebook .jp-Cell {
    @media ${MaterialBreakpointUp.TABLET} {
      margin-left: ${EditorMargin.DESKTOP};
      margin-right: ${EditorMargin.DESKTOP};
    }

    @media ${MaterialBreakpointDown.TABLET} {
      margin-left: ${EditorMargin.TABLET};
      margin-right: ${EditorMargin.TABLET};
    }

    @media ${MaterialBreakpointDown.PHONE} {
      margin-left: ${EditorMargin.PHONE};
      margin-right: ${EditorMargin.PHONE};
    }
  }

  /* Adjust input prompt styles. */
  .jp-Notebook .jp-Cell .jp-InputPrompt {
    cursor: pointer;
    max-width: 48px !important;
    display: flex !important;
    flex-flow: row nowrap !important;
    justify-content: center !important;
  }

  ${
    /* sc-selector */ LIGHT_SELECTOR
  } .jp-Notebook.jp-mod-commandMode .jp-Cell.jp-mod-selected {
    background: #F8F9FA;
  }

  ${
    /* sc-selector */ DARK_SELECTOR
  } .jp-Notebook.jp-mod-commandMode .jp-Cell.jp-mod-selected {
    background: #3C4043;
  }

  ${/* sc-selector */ LIGHT_SELECTOR} .jp-Notebook .jp-Cell .jp-InputPrompt {
    color: ${BLACK};
  }

  ${/* sc-selector */ DARK_SELECTOR} .jp-Notebook .jp-Cell .jp-InputPrompt {
    color: ${WHITE};
  }

  /*** CONTEXT MENU ***/

  /* Context menu styles. */
  .lm-Widget.p-Widget.lm-Menu.p-Menu {
    border-radius: 0 0 8px 8px;
  }

  /* Context menu item styles. */
  li.lm-Menu-item.p-Menu-item {
    width: 320px;
    padding: 0;
    line-height: 48px;
    min-height: 0;
    margin-top: 0 !important;
  }

  /* Context menu divider styles. */
  ${
    /* sc-selector */ LIGHT_SELECTOR
  } .lm-Menu-item[data-type='separator'] > div::after {
    border-top: 1px solid #DADCE0;
  }

  /*** NAV REMOVAL ***/

  /* Hide top menu bar / logo. */
  div#jp-top-panel {
    display: none;
  }

  /* Hide left sidebar. */
  .lm-Widget.p-Widget.lm-TabBar.p-TabBar.jp-SideBar.jp-mod-left.lm-BoxPanel-child.p-BoxPanel-child {
    display: none;
  }

  /*** EXPAND / COLLAPSE ***/

  .jp-Placeholder-content {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    height: 35px !important;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0px;
    cursor: pointer;
  }

  ${/* sc-selector */ LIGHT_SELECTOR} .jp-Placeholder-content {
    color: ${BLACK} !important;
  }

  ${/* sc-selector */ DARK_SELECTOR} .jp-Placeholder-content {
    color: ${WHITE} !important;
  }

  .jp-Placeholder-content:before {
    position: absolute;
    visibility: visible;
    top: 0;
    left: 0;
    width: 48px;
    height: 100%;
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  ${/* sc-selector */ LIGHT_SELECTOR} .jp-Placeholder-content:before {
    content: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAQCAYAAAAvf+5AAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAB9SURBVHgB1ZLREYAgCIah2qNGsTZpskZpFQepI0G7EPV69nsS+O9XQADDkrB5tCLA8ZSA7tUHCmEmelFirDlFi9wZW9fZ/BAP6O2b5MzuXOsA5xykrqcD6Np1M4yuxa6BZBR6dd94aOb4b+AU4k0GXjoo7AqrYrOAgtY3ewB48VzK7ycK6AAAAABJRU5ErkJggg==');
  }

  ${/* sc-selector */ DARK_SELECTOR} .jp-Placeholder-content:before {
    content: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAQCAYAAAAvf+5AAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAABsSURBVHgBzVLBDYAwCIQuoqt0cjbRboI0UqMX0PgrCSmBA3JcicBUde1Ob+ag3f0B5jvIHoHeyswtnRROztZhvni+4RqPq9emNyNDxdlJpMaoWXix7iDB89ApwIKd0cG30fxPwmwyfTAMv9kBP1+l98FgYj8AAAAASUVORK5CYII=');
  }
  
  /* Collapsers (left gutter). General styles. */
  .jp-Notebook .jp-Cell.jp-mod-active .jp-Collapser {
    border-radius: 8px 0 0 8px;
    cursor: pointer;
  }

  ${/* sc-selector */ LIGHT_SELECTOR} {
    /* Collapsers (left gutter). */
    .jp-Notebook .jp-Cell.jp-mod-active .jp-Collapser {
      background: ${COLLAPSER_LIGHT};
    }
    .jp-Notebook .jp-Cell:not(.jp-mod-active) .jp-Collapser:hover {
      background: ${COLLAPSER_LIGHT_HOVER};
    }
  }

  ${/* sc-selector */ DARK_SELECTOR} {
    /* Collapsers (left gutter). */
    .jp-Notebook .jp-Cell.jp-mod-active .jp-Collapser {
      background: ${COLLAPSER_DARK};
    }
    .jp-Notebook .jp-Cell:not(.jp-mod-active) .jp-Collapser:hover {
      background: ${COLLAPSER_DARK_HOVER};
    }
  }

  .jp-Placeholder-prompt.jp-OutputPrompt {
    max-width: 48px;
  }

  .lm-Widget.p-Widget.jp-OutputPrompt.jp-OutputArea-prompt {
    max-width: 48px;
  }

  /* Cell header and footer both need to allow content to spill into next cell (hover buttons). */
  .jp-CellHeader {
    overflow: visible;
  }

  .jp-CellFooter {
    height: 32px !important;
    overflow: visible !important;
  }

  /* Make the JupyterLab find tool visible. */
  .lm-Widget.p-Widget.jp-DocumentSearch-overlay {
    position: fixed;
    min-width: 480px;
    right: 0;
  }
  
  /*** DRAG ***/

  .lm-mod-drag-image.p-mod-drag-image:before {
    visibility: visible;
    position: absolute;
    box-sizing: border-box;
    left: 0;
    top: 0;
    width: 8px;
    height: 100%;
    margin-left: -8px;
    border-radius: 8px 0px 0px 8px;
    content: " ";
  }

  ${
    /* sc-selector */ LIGHT_SELECTOR
  } .lm-mod-drag-image.p-mod-drag-image:before {
    background: ${COLLAPSER_LIGHT};
  }

  ${
    /* sc-selector */ DARK_SELECTOR
  } .lm-mod-drag-image.p-mod-drag-image:before {
    background: ${COLLAPSER_DARK};
  }

  .lm-mod-drag-image.p-mod-drag-image {
    box-sizing: border-box;
    border: 0;
    margin-left: -24px;
    margin-top: -28px;
    height: auto;
    padding: 0;
    cursor: move !important;
  }

  .jp-dragImage.jp-dragImage-singlePrompt {
    box-sizing: border-box;
    border: 0;
    border-radius: 0 8px 8px 0;
    height: 56px;
    width: auto;
    cursor: move !important;
    ${shadow()};
  }

  ${/* sc-selector */ LIGHT_SELECTOR} .jp-dragImage.jp-dragImage-singlePrompt {
    background: ${WHITE};
  }

  ${/* sc-selector */ DARK_SELECTOR} .jp-dragImage.jp-dragImage-singlePrompt {
    background: ${darkPalette.windowBackground};
  }

  /* Hide the original input prompt: "[]:" */
  span.jp-dragImage-prompt {
    visibility: hidden;
    position: relative;
    box-sizing: border-box;
    width: 48px;
    min-width: 48px;
    overflow: hidden;
    margin: 0;
    cursor: move !important;
  }

  /* Replacement input prompt. */
  span.jp-dragImage-prompt:after {
    visibility: visible;
    position: absolute;
    box-sizing: border-box;
    top: 0;
    left: 0;
    width: 48px !important;
    max-width: 48px !important;
    margin: 0;
    padding: 5px;
    display: flex !important;
    flex: 0 0 48px;
    flex-flow: row nowrap !important;
    justify-content: center !important;
    text-align: center;
    cursor: move !important;
  }

  /* Replace input prompt with base64 inlined material drag icons (pseudo selector). */
  ${/* sc-selector */ LIGHT_SELECTOR} span.jp-dragImage-prompt:after {
    content: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAQCAYAAAAvf+5AAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAACESURBVHgB3Y+xDYNAEAR33xKpKcGV2YFJHDiwK4AK7IwIISqDDvgG4DhAgEACPiBis509nTS8P18Pov4DtAYM0yTOoFlyQ0ioxdftVndDnyU3gPjDCKHFVGbcUPiBoICI5QXR+HGFHxeeyZp7B0HwpudV4moduVr/tqyvrtZf5bnysuUNRMR591pgCUAAAAAASUVORK5CYII=');
  }

  ${/* sc-selector */ DARK_SELECTOR} span.jp-dragImage-prompt:after {
    content: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAQCAYAAAAvf+5AAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAABfSURBVHgB3Y9RCoAwDEPrLqA3tCefN3DeoKZQsWWD/uxrgUB4ZB0hETnhBl+aydRxC5+aKwZewA769bgcOdpsr/UbdheHfKJWWr1lBVzTjpRsNWKFecrqPfBkdYVvzS+PhhCla7daYQAAAABJRU5ErkJggg==');
  }

  .jp-dragImage span.jp-dragImage-content {
    box-sizing: content-box;
    border-radius: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 205px;
    width: auto;
    height: 30px;
    padding: 12px 16px;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    font-family: 'Roboto Mono', monospace !important;
    font-size: 14px !important;
    line-height: 20px !important;
  }

  ${/* sc-selector */ LIGHT_SELECTOR} .jp-dragImage span.jp-dragImage-content {
    background: ${LIGHT_CELL_BACKGROUND} !important;
    border: 1px solid #DADCE0 !important;
  }

  ${/* sc-selector */ DARK_SELECTOR} .jp-dragImage span.jp-dragImage-content {
    background: #202124 !important;
    border: 1px solid #5F6368 !important;
  }

  /* Override the cursor universally when in dragging state. */
  body.lm-mod-override-cursor.p-mod-override-cursor {
    cursor: move !important;
  }

  /* Set the drop border color to Kaggle blue. */
  .jp-mod-dropTarget {
    border-top-color: #20BEFF !important;
  }

  /* Hide the normal cell during drag. */
  .jp-mod-dropSource {
    display: none !important;
  }

  /*** MIRRORMARK ***/

  /* Add margin to bottom of markdown cell to account for markdown toolbar. */
  .jp-MarkdownCell.jp-Notebook-cell.jp-mod-active.jp-mod-selected .CodeMirror.cm-s-jupyter.CodeMirror-wrap {
    margin-bottom: 40px;
  }

  /* Fix the markdown toolbar. */
  body .mirrormark-toolbar {
    position: absolute;
    height: 40px;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 99;
    cursor: default;
    border: 0;
  }

  ${/* sc-selector */ LIGHT_SELECTOR} .mirrormark-toolbar {
    background: ${WHITE};
    border-top: 1px solid #dedfe0;
    color: ${BLACK};
  }

  ${/* sc-selector */ DARK_SELECTOR} .mirrormark-toolbar {
    background: #202124;
    border-top: 1px solid #5F6368;
    color: ${WHITE};
  }

  ${/* sc-selector */ DARK_SELECTOR} .mirrormark-toolbar li a {
    background: #202124;
    color: ${WHITE};

    &:hover {
      color: ${WHITE};
      background: #5F6368;
    }
  }

  /* Following styles are mirrormark (markdown cells). */
  .cm-s-mirrormark .cm-m-markdown {
    color: #000;
    font-family: sans-serif;
  }
  .cm-s-mirrormark .cm-m-markdown.cm-header {
    font-family: Menlo, Consolas, Monaco, 'Andale Mono', monospace;
    font-weight: bold;
  }
  .cm-s-mirrormark .cm-m-markdown.cm-header-1 {
    font-size: 28px;
  }
  .cm-s-mirrormark .cm-m-markdown.cm-header-2 {
    font-size: 22px;
    /*
    font-size: 18px;
    */
  }
  .cm-s-mirrormark .cm-m-markdown.cm-quote {
    color: #888;
    background-color: rgba(128, 128, 128, 0.05);
    padding: 5px;
  }
  .cm-s-mirrormark .cm-m-markdown.cm-quote,
  .cm-s-mirrormark .cm-m-markdown.cm-string {
    color: #888;
  }
  .cm-s-mirrormark .cm-m-markdown.cm-tag,
  .cm-s-mirrormark .cm-m-markdown.cm-link {
    color: #444;
  }
  .cm-s-mirrormark .cm-m-markdown.cm-comment {
    color: #a50;
    font-family: Menlo, Consolas, Monaco, 'Andale Mono', monospace;
  }
  .cm-s-mirrormark .cm-m-markdown.cm-m-xml,
  .cm-s-mirrormark .cm-m-markdown.cm-bracket {
    color: #170;
    font-family: inherit;
  }
  .cm-s-mirrormark.CodeMirror.CodeMirror-has-preview .CodeMirror-scroll {
    display: none;
  }
  .cm-s-mirrormark.CodeMirror.CodeMirror-has-preview .CodeMirror-preview {
    display: block;
  }
  .cm-s-mirrormark.CodeMirror .CodeMirror-preview {
    display: none;
    /* This was copy pasted from mirrormark repository with important. */
    height: auto !important;
    /* This was copy pasted from mirrormark repository with important. */
    overflow: visible !important;
    padding: 30px 4%;
    box-sizing: border-box;
    font-family: sans-serif;
  }
  .cm-s-mirrormark.CodeMirror .CodeMirror-preview h1,
  .cm-s-mirrormark.CodeMirror .CodeMirror-preview h2,
  .cm-s-mirrormark.CodeMirror .CodeMirror-preview h3 {
    font-family: Menlo, Consolas, Monaco, 'Andale Mono', monospace;
  }
  .cm-s-mirrormark.CodeMirror .CodeMirror-preview pre,
  .cm-s-mirrormark.CodeMirror .CodeMirror-preview code {
    font-family: Menlo, Consolas, Monaco, 'Andale Mono', monospace;
  }
  .cm-s-mirrormark.CodeMirror .CodeMirror-fullscreen .CodeMirror-preview {
    /* This was copy pasted from mirrormark repository with important. */
    height: 100% !important;
    /* This was copy pasted from mirrormark repository with important. */
    overflow: scroll !important;
  }
  .mirrormark-toolbar li > a {
    display: inline-block;
    /*
    font-family: normal normal normal 14px/1 FontAwesome;
    */
    font-size: inherit;
    text-rendering: auto;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    transform: translate(0, 0);
  }
  .mirrormark-toolbar li > a.pull-left {
    margin-right: .3em;
  }
  .mirrormark-toolbar li > a.pull-right {
    margin-left: .3em;
  }
  .mirrormark-toolbar .bold a:before {
    content: "\f032";
  }
  .mirrormark-toolbar .italicize a:before {
    content: "\f033";
  }
  .mirrormark-toolbar .blockquote a:before {
    content: "\f10d";
  }
  .mirrormark-toolbar .strikethrough a:before {
    content: "\f0cc";
  }
  .mirrormark-toolbar .link a:before {
    content: "\f0c1";
  }
  .mirrormark-toolbar .image a:before {
    content: "\f03e";
  }
  .mirrormark-toolbar .unorderedList a:before {
    content: "\f03a";
  }
  .mirrormark-toolbar .orderedList a:before {
    content: "\f0cb";
  }
  .mirrormark-toolbar .fullScreen a:before {
    content: "\f065";
  }
  .mirrormark-toolbar .preview a:before {
    content: "\f15b";
  }
  .cm-s-mirrormark.CodeMirror-fullscreen .mirrormark-toolbar .fullScreen a:before {
    content: "\f066";
  }
  .cm-s-mirrormark.CodeMirror-has-preview .mirrormark-toolbar .preview a:before {
    content: "\f016";
  }
  /* Toolbar Theme */
  .mirrormark-toolbar {
    background: #fff;
    border: 1px solid #dedfe0;
    display: block;
    margin-top: 0;
    margin-bottom: 0;
    padding-left: 0;
    padding-right: 0;
    border-top-right-radius: 4px;
  }
  .mirrormark-toolbar li {
    display: inline-block;
    position: relative;
    z-index: 1;
  }
  .mirrormark-toolbar li.fullScreen,
  .mirrormark-toolbar li.preview {
    display: none; /* added by RM to hide toolbar item */
    float: right;
  }
  .mirrormark-toolbar li.has-nested:after {
    color: #888;
    position: absolute;
    bottom: 3px;
    right: 3px;
    font-size: 11px;
    transform: scale(1.3, 1);
  }
  .mirrormark-toolbar li a {
    background-color: #fff;
    color: #888;
    cursor: pointer;
    display: block;
    font-size: 16px;
    height: 40px;
    line-height: 40px;
    text-align: center;
    transition: color .2s linear;
    width: 40px;
  }
  .mirrormark-toolbar li:hover a,
  .mirrormark-toolbar li a:hover,
  .mirrormark-toolbar li:hover.has-nested:after {
    color: #000;
    text-decoration: none;
  }
  .mirrormark-toolbar li:hover ul {
    display: block;
  }
  .mirrormark-toolbar li ul {
    background: #e5e5e5;
    display: none;
    position: absolute;
    top: 41px;
    width: 150px;
    z-index: 1000;
  }
  .mirrormark-toolbar li ul li {
    float: none;
    width: 100%;
  }
  .mirrormark-toolbar li ul li a {
    background: transparent;
    /* This was copy pasted from mirrormark repository with important. */
    color: #888 !important;
    font-family: "Helvetica Neue", sans-serif;
    font-size: 12px;
    height: auto;
    line-height: normal;
    padding: .25rem .75rem;
    text-align: left;
    width: auto;
  }
  .mirrormark-toolbar li ul li:hover a {
    /* This was copy pasted from mirrormark repository with important. */
    color: black !important;
  }
  .cm-s-mirrormark {
    font: 16px/1.7 Menlo, Consolas, Monaco, 'Andale Mono', monospace;
    box-sizing: border-box;
    height: auto;
    margin: auto;
    position: relative;
    z-index: 0;
  }
  .cm-s-mirrormark .CodeMirror-scroll {
    /* This was copy pasted from mirrormark repository with important. */
    height: auto !important;
    /* This was copy pasted from mirrormark repository with important. */
    overflow: visible !important;
    padding: 30px 4%;
    box-sizing: border-box;
  }
  .cm-s-mirrormark.CodeMirror-fullscreen .CodeMirror-scroll {
    /* This was copy pasted from mirrormark repository with important. */
    height: 100% !important;
    /* This was copy pasted from mirrormark repository with important. */
    overflow: scroll !important;
  }
  .cm-s-mirrormark pre.CodeMirror-placeholder {
    color: #999;
  }
  /* stylelint-enable selector-type-no-unknown */
`;
